import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@clerk/clerk-react';
import AdminAPI from '../../utils/adminApi';

const BookingManagementContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 24px 24px 0 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border: 1px solid rgba(102, 126, 234, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: translateY(-2px);
  }
`;

const Title = styled.h2`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 2rem;
  margin: 0;
  font-weight: 700;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const BookingTable = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(102, 126, 234, 0.2);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: rgba(102, 126, 234, 0.1);
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid rgba(102, 126, 234, 0.2);
`;

const BookingRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(102, 126, 234, 0.05);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  
  &.pending {
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white;
  }
  
  &.confirmed {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
  }
  
  &.cancelled {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    color: white;
  }
  
  &.refunded {
    background: linear-gradient(135deg, #9b59b6, #8e44ad);
    color: white;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  margin: 0.25rem;
  
  &.view {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    border: 1px solid rgba(102, 126, 234, 0.2);
    
    &:hover {
      background: rgba(102, 126, 234, 0.2);
    }
  }
  
  &.edit {
    background: rgba(52, 152, 219, 0.1);
    color: #3498db;
    border: 1px solid rgba(52, 152, 219, 0.2);
    
    &:hover {
      background: rgba(52, 152, 219, 0.2);
    }
  }
  
  &.delete {
    background: rgba(231, 76, 60, 0.1);
    color: #e74c3c;
    border: 1px solid rgba(231, 76, 60, 0.2);
    
    &:hover {
      background: rgba(231, 76, 60, 0.2);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 12px;
  padding: 1rem;
  color: #e74c3c;
  text-align: center;
  margin: 1rem 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.textLight};
  
  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.2rem;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

// Modal Components
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
  overflow-y: auto;
  /* Sicherstellen, dass es über allem anderen liegt */
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  position: relative;
  margin: auto;
  transform: translateY(0);
  /* Sicherstellen, dass es mittig im Viewport ist */
  position: relative !important;
  margin: auto !important;
  flex-shrink: 0;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #333;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 0.75rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.6rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.6rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  min-height: 60px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
`;

const ModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
    }
  }
  
  &.secondary {
    background: rgba(0, 0, 0, 0.1);
    color: #374151;
    
    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
  }
  
  &.danger {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(231, 76, 60, 0.3);
    }
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const InfoValue = styled.span`
  color: #6b7280;
`;

const BookingManagement = ({ onBack, initialCourseFilter = null }) => {
  const { getToken } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    course_id: initialCourseFilter || ''
  });
  const [courses, setCourses] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalType, setModalType] = useState(null); // 'view', 'edit', 'delete'
  const [editingBooking, setEditingBooking] = useState(null);

  useEffect(() => {
    loadBookings();
    loadCourses();
  }, [filters]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      let token;
      try {
        token = await getToken();
      } catch (err) {
        // Fallback für Development
        token = 'dev-clerk-token';
      }
      
      const response = await AdminAPI.getBookings(filters, token);
      setBookings(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }
      
      const response = await AdminAPI.getCourses(token);
      setCourses(response.data || []);
    } catch (err) {
      console.error('Error loading courses:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatPrice = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      cancelled: 'Storniert',
      refunded: 'Erstattet'
    };
    return statusMap[status] || status;
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setModalType('view');
  };

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setEditingBooking({ ...booking });
    setModalType('edit');
  };

  const handleDeleteBooking = (booking) => {
    setSelectedBooking(booking);
    setModalType('delete');
  };

  const handleCloseModal = () => {
    setModalType(null);
    setSelectedBooking(null);
    setEditingBooking(null);
  };

  const handleSaveBooking = async () => {
    if (!editingBooking) return;

    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      await AdminAPI.updateBooking(editingBooking.id, editingBooking, token);
      await loadBookings(); // Reload bookings
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedBooking) return;

    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      await AdminAPI.deleteBooking(selectedBooking.id, token);
      await loadBookings(); // Reload bookings
      handleCloseModal();
    } catch (err) {
      if (err.message.includes('CONFIRMED_BOOKING_CANNOT_DELETE')) {
        setError('Bestätigte Buchungen können nicht gelöscht werden. Bitte stornieren Sie die Buchung zuerst.');
      } else {
        setError(err.message);
      }
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;

    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      await AdminAPI.updateBookingStatus(selectedBooking.id, 'cancelled', 'Storniert durch Admin', token);
      await loadBookings(); // Reload bookings
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStatusChange = async (bookingId, newStatus, reason = '') => {
    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      await AdminAPI.updateBookingStatus(bookingId, newStatus, reason, token);
      await loadBookings(); // Reload bookings
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <BookingManagementContainer>
        <LoadingSpinner>Buchungen werden geladen...</LoadingSpinner>
      </BookingManagementContainer>
    );
  }

  return (
    <>
      <BookingManagementContainer>
        <Header>
          <HeaderLeft>
            <BackButton onClick={onBack}>
              ← Zurück zum Dashboard
            </BackButton>
            <Title>Buchungsverwaltung</Title>
          </HeaderLeft>
        </Header>

        <FilterSection>
          <FilterSelect
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">Alle Status</option>
            <option value="pending">Ausstehend</option>
            <option value="confirmed">Bestätigt</option>
            <option value="cancelled">Storniert</option>
            <option value="refunded">Erstattet</option>
          </FilterSelect>
          
          <FilterSelect
            value={filters.course_id}
            onChange={(e) => handleFilterChange('course_id', e.target.value)}
          >
            <option value="">Alle Kurse</option>
            {courses.map(course => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </FilterSelect>
        </FilterSection>

        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        {bookings.length === 0 ? (
          <EmptyState>
            <h3>Keine Buchungen vorhanden</h3>
            <p>Es wurden keine Buchungen gefunden, die den aktuellen Filtern entsprechen.</p>
          </EmptyState>
        ) : (
          <BookingTable>
            <TableHeader>
              <div>ID</div>
              <div>Kunde</div>
              <div>Kurs</div>
              <div>Status</div>
              <div>Betrag</div>
              <div>Datum</div>
              <div>Aktionen</div>
            </TableHeader>
            
            <AnimatePresence>
              {bookings.map((booking, index) => (
                <BookingRow
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <div>#{booking.id}</div>
                  <div>
                    <div style={{ fontWeight: '600' }}>{booking.customer_name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{booking.customer_email}</div>
                  </div>
                  <div>{booking.course_title || 'Unbekannter Kurs'}</div>
                  <div>
                    <StatusBadge className={booking.status}>
                      {getStatusText(booking.status)}
                    </StatusBadge>
                  </div>
                  <div>{formatPrice(booking.amount, booking.currency)}</div>
                  <div>{formatDate(booking.created_at)}</div>
                  <div>
                    <ActionButton className="view" onClick={() => handleViewBooking(booking)}>Ansehen</ActionButton>
                    <ActionButton className="edit" onClick={() => handleEditBooking(booking)}>Bearbeiten</ActionButton>
                    <ActionButton className="delete" onClick={() => handleDeleteBooking(booking)}>Löschen</ActionButton>
                  </div>
                </BookingRow>
              ))}
            </AnimatePresence>
          </BookingTable>
        )}
      </BookingManagementContainer>

      {/* Modals - Außerhalb des Containers für bessere Positionierung */}
      <AnimatePresence>
        {modalType === 'view' && selectedBooking && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.8, y: -50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -50 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>📋 Buchungsdetails</ModalTitle>
                <CloseButton onClick={handleCloseModal}>×</CloseButton>
              </ModalHeader>
              
              <ModalBody>
                <InfoRow>
                  <InfoLabel>Buchungs-ID:</InfoLabel>
                  <InfoValue>#{selectedBooking.id}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Kunde:</InfoLabel>
                  <InfoValue>{selectedBooking.customer_name}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>E-Mail:</InfoLabel>
                  <InfoValue>{selectedBooking.customer_email}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Telefon:</InfoLabel>
                  <InfoValue>{selectedBooking.customer_phone || 'Nicht angegeben'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Kurs:</InfoLabel>
                  <InfoValue>{selectedBooking.course_title || 'Unbekannter Kurs'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Status:</InfoLabel>
                  <InfoValue>
                    <StatusBadge className={selectedBooking.status}>
                      {getStatusText(selectedBooking.status)}
                    </StatusBadge>
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Betrag:</InfoLabel>
                  <InfoValue>{formatPrice(selectedBooking.amount, selectedBooking.currency)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Buchungsdatum:</InfoLabel>
                  <InfoValue>{formatDate(selectedBooking.created_at)}</InfoValue>
                </InfoRow>
                {selectedBooking.child_name && (
                  <InfoRow>
                    <InfoLabel>Kind:</InfoLabel>
                    <InfoValue>{selectedBooking.child_name} {selectedBooking.child_age && `(${selectedBooking.child_age} Jahre)`}</InfoValue>
                  </InfoRow>
                )}
                {selectedBooking.notes && (
                  <InfoRow>
                    <InfoLabel>Notizen:</InfoLabel>
                    <InfoValue>{selectedBooking.notes}</InfoValue>
                  </InfoRow>
                )}
              </ModalBody>
              
              <ModalActions>
                <ModalButton className="secondary" onClick={handleCloseModal}>
                  Schließen
                </ModalButton>
                <ModalButton className="primary" onClick={() => handleEditBooking(selectedBooking)}>
                  Bearbeiten
                </ModalButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        {modalType === 'edit' && editingBooking && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.8, y: -50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -50 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>✏️ Buchung bearbeiten</ModalTitle>
                <CloseButton onClick={handleCloseModal}>×</CloseButton>
              </ModalHeader>
              
              <ModalBody>
                <FormGroup>
                  <Label>Kundenname *</Label>
                  <Input
                    value={editingBooking.customer_name}
                    onChange={(e) => setEditingBooking({...editingBooking, customer_name: e.target.value})}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>E-Mail *</Label>
                  <Input
                    type="email"
                    value={editingBooking.customer_email}
                    onChange={(e) => setEditingBooking({...editingBooking, customer_email: e.target.value})}
                  />
                </FormGroup>
                
                <FormRow>
                  <FormGroup>
                    <Label>Telefon</Label>
                    <Input
                      value={editingBooking.customer_phone || ''}
                      onChange={(e) => setEditingBooking({...editingBooking, customer_phone: e.target.value})}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Kindername</Label>
                    <Input
                      value={editingBooking.child_name || ''}
                      onChange={(e) => setEditingBooking({...editingBooking, child_name: e.target.value})}
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>Status</Label>
                    <Select
                      value={editingBooking.status}
                      onChange={(e) => setEditingBooking({...editingBooking, status: e.target.value})}
                    >
                      <option value="pending">Ausstehend</option>
                      <option value="confirmed">Bestätigt</option>
                      <option value="cancelled">Storniert</option>
                      <option value="refunded">Erstattet</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Betrag</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingBooking.amount}
                      onChange={(e) => setEditingBooking({...editingBooking, amount: parseFloat(e.target.value)})}
                    />
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <Label>Notizen</Label>
                  <TextArea
                    value={editingBooking.notes || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, notes: e.target.value})}
                    placeholder="Zusätzliche Notizen zur Buchung..."
                  />
                </FormGroup>
              </ModalBody>
              
              <ModalActions>
                <ModalButton className="secondary" onClick={handleCloseModal}>
                  Abbrechen
                </ModalButton>
                <ModalButton className="primary" onClick={handleSaveBooking}>
                  Speichern
                </ModalButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        {modalType === 'delete' && selectedBooking && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.8, y: -50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -50 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  {selectedBooking.status === 'confirmed' ? '🚫 Buchung stornieren' : '⚠️ Buchung löschen'}
                </ModalTitle>
                <CloseButton onClick={handleCloseModal}>×</CloseButton>
              </ModalHeader>
              
              <ModalBody>
                <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                  {selectedBooking.status === 'confirmed' ? (
                    <>Sind Sie sicher, dass Sie die <strong>bestätigte</strong> Buchung <strong>#{selectedBooking.id}</strong> von <strong>{selectedBooking.customer_name}</strong> <strong>stornieren</strong> möchten?</>
                  ) : (
                    <>Sind Sie sicher, dass Sie die Buchung <strong>#{selectedBooking.id}</strong> von <strong>{selectedBooking.customer_name}</strong> <strong>löschen</strong> möchten?</>
                  )}
                </p>
                {selectedBooking.status === 'confirmed' && (
                  <div style={{ 
                    background: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginBottom: '1rem',
                    color: '#856404'
                  }}>
                    <strong>ℹ️ Info:</strong> Die Buchung wird <strong>storniert</strong> und kann später gelöscht werden. 
                    Die Kurskapazität wird wieder freigegeben.
                  </div>
                )}
                {selectedBooking.status === 'cancelled' && (
                  <div style={{ 
                    background: '#f8d7da', 
                    border: '1px solid #f5c6cb', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginBottom: '1rem',
                    color: '#721c24'
                  }}>
                    <strong>⚠️ Warnung:</strong> Diese Buchung ist bereits <strong>storniert</strong>. 
                    Das Löschen entfernt sie endgültig aus dem System.
                  </div>
                )}
                <p style={{ marginBottom: '1rem', color: '#e74c3c', fontWeight: '600' }}>
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
                <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong>Buchungsdetails:</strong>
                  <br />
                  Kurs: {selectedBooking.course_title || 'Unbekannter Kurs'}
                  <br />
                  Status: {getStatusText(selectedBooking.status)}
                  <br />
                  Betrag: {formatPrice(selectedBooking.amount, selectedBooking.currency)}
                </div>
              </ModalBody>
              
              <ModalActions>
                <ModalButton className="secondary" onClick={handleCloseModal}>
                  Abbrechen
                </ModalButton>
                {selectedBooking.status === 'confirmed' ? (
                  <ModalButton className="danger" onClick={handleCancelBooking}>
                    Stornieren
                  </ModalButton>
                ) : (
                  <ModalButton className="danger" onClick={handleConfirmDelete}>
                    Löschen
                  </ModalButton>
                )}
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default BookingManagement;
